// CareerSection.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import styles from "./CareerSection.module.css";
import templatePt from "../../data/pt/career_templates.json";
import templateEn from "../../data/en/career_templates.json";
import { renderText, renderArray } from "../../utils/templateRenderer";

/* fallback generator (simple, used if no template) */
function fallbackGenerate({ temperament, characterTraits, chosenCharacter }) {
  const [E, A, S] = temperament || [50, 50, 50];
  const intro = `Seu perfil combina Emotionality=${E}%, Activity=${A}%, Sociability=${S}%.`;
  return {
    intro,
    strengths: ["Sensibilidade ao contexto", "Capacidade de adaptação"],
    challenges: ["Manter rotina consistente", "Lidar com pressão emocional"],
    recommendations: ["Defina micro-objetivos", "Revisão semanal breve"],
    hints: [],
  };
}

export default function CareerSection({
  premiumPayload = null,
  insights = null,
  derivedArchetype = null,
  wrapPremium = null,
}) {
  const { i18n, t } = useTranslation("user");
  // determine archetype key (try several places)
  const template = i18n.language?.startsWith("pt") ? templatePt : templateEn;
  const archeKey = useMemo(() => {
    const candidate =
      (derivedArchetype && (derivedArchetype.key || derivedArchetype.label)) ||
      "default";
    return String(candidate).toLowerCase().replace(/\s+/g, "-");
  }, [derivedArchetype]);

  // context to render templates
  const context = useMemo(
    () => ({
      userName: (premiumPayload && premiumPayload.name) || "Usuário",
      personalityTraits: (premiumPayload &&
        premiumPayload.personalityTraits) || [50, 50, 50],
      characterTraits: (premiumPayload && premiumPayload.characterTraits) || {
        discipline: 50,
        mentalOpenness: 50,
        emotionalOpenness: 50,
        perspective: 50,
      },
      chosenCharacter: (premiumPayload && premiumPayload.chosenCharacter) || {},
      insights: insights || {},
    }),
    [premiumPayload, insights, derivedArchetype, archeKey]
  );

  // select template (or default)
  const tpl = template[archeKey] || template["default"] || null;

  // rendered pieces (use renderText/renderArray utils)
  const assembled = useMemo(() => {
    if (!tpl || !tpl.career) {
      // fallback to generator
      return fallbackGenerate({
        temperament: context.personalityTraits.slice(0, 3),
        characterTraits: context.characterTraits,
        chosenCharacter: context.chosenCharacter,
      });
    }
    const career = tpl.career;
    const intro = renderText(career.intro || "", context) || "";
    const strengths = renderArray(career.strengths || [], context);
    const challenges = renderArray(career.challenges || [], context);
    const recommendations = renderArray(career.recommendations || [], context);
    const hints = renderArray(career.hints || [], context);
    return { intro, strengths, challenges, recommendations, hints };
  }, [tpl, context]);

  return (
    <section className={styles.careerSection}>
      <header className={styles.header}>
        <h3>{t("profile.careerTitle")}</h3>
      </header>
      <div className={styles.grid}>
        <div className={styles.col}>
          <h4>{t("profile.careerSubtitle")}</h4>
          <p className={styles.lead}>{assembled.intro}</p>

          <h5>{t("profile.careerPoints")}</h5>
          <ul className={styles.points}>
            {assembled.strengths.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
          <h5>{t("profile.careerDangerPoints")}</h5>
          <ul className={styles.pointsDanger}>
            {assembled.challenges.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>

        <div className={styles.col}>
          <h5 style={{ marginTop: 12 }}>{t("profile.careerRecomendations")}</h5>

          <div>
            {wrapPremium(
              <ol className={styles.recs}>
                {assembled.recommendations.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ol>
            )}
            <h5 style={{ marginTop: 12 }}>{t("profile.careerHints")}</h5>
            {wrapPremium(
              assembled.hints && assembled.hints.length > 0 && (
                <>
                  <div className={styles.hints}>
                    {assembled.hints.map((h, i) => (
                      <span key={i} className={styles.hintBadge}>
                        {h}
                      </span>
                    ))}
                  </div>
                </>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
